/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-25 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ButtonGroup, Button, Icon, Tooltip, InlineError } from '@shopify/polaris';
import {
  MobileVerticalDotsMajor,
  QuestionMarkMajor,
  PlusMinor,
  MinusMinor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Field, getFormValues, change } from 'redux-form';

import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import SubCategoryTableCell from './sub-category-table-cell';

const RenderTooltip = ({ content }) => {
  return (
    <span className="tooltip">
      <Tooltip
        content={<span className="tooltipContent">{content}</span>}
        preferredPosition="above">
        <Icon source={QuestionMarkMajor} color="base" />
      </Tooltip>
    </span>
  );
};

const FieldWithI18n = withI18n()(({ i18n, placeholder, ...rest }) => (
  <Field {...rest} placeholder={i18n.translate(placeholder)} />
));

type Props = {
  items: any[]
};

type ComposedProps = Props & WithI18nProps;

class SubCategoryTable extends Component<ComposedProps, State> {
  onDragEnd = (result: DropResult) => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
  };

  render() {
    const { fields, meta, i18n } = this.props;
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <div className="Polaris-DataTable BC-DataTable__ManualValues">
          <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
            <table className="Polaris-DataTable__Table">
              <thead>
                <tr>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn Polaris-DataTable__Cell--header" />
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                    {i18n.translate('AdminHTML.FilterTree.Common.collection')}
                    <RenderTooltip
                      content={i18n.translate(
                        'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_SubCategoryTable_TableHeading_Collection_RenderTooltip_Content'
                      )}
                    />
                  </th>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                    {i18n.translate(
                      'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_SubCategoryTable_TableHeading_Tags'
                    )}
                  </th>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric"></th>
                </tr>
              </thead>
              <Droppable droppableId="table">
                {provided => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {fields.map((item, key) => {
                      return (
                        // Enable drag for per option row
                        <Draggable draggableId={`item_${key}`} key={`item_${key}`} index={key}>
                          {(provided, snapshot) => (
                            <tr
                              className="Polaris-DataTable__TableRow"
                              ref={provided.innerRef}
                              {...provided.draggableProps}>
                              {/* Drag icon */}
                              <SubCategoryTableCell
                                isDragOccurring={snapshot.isDragging}
                                className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                <div {...provided.dragHandleProps}>
                                  <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                </div>
                              </SubCategoryTableCell>

                              {/* Collection */}
                              <SubCategoryTableCell
                                isDragOccurring={snapshot.isDragging}
                                className="Polaris-DataTable__Cell">
                                <FieldWithI18n
                                  name={`${item}.collection`}
                                  component={RenderSelect2}
                                  options={this.getCollectionList(key)}
                                  placeholder="AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_SubCategoryTable_SubCategoryTableCell1_Placeholder"
                                />
                              </SubCategoryTableCell>

                              {/* Filter label*/}
                              <SubCategoryTableCell
                                isDragOccurring={snapshot.isDragging}
                                className="Polaris-DataTable__Cell">
                                <FieldWithI18n
                                  name={`${item}.tags`}
                                  component={RenderAutocompleteV2}
                                  value={fields.get(key).tags || []}
                                  type={'tags'}
                                  filterPrefix={''}
                                  excludedValues={[]}
                                  placeholder="AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_SubCategoryTable_SubCategoryTableCell2_Placeholder"
                                />
                              </SubCategoryTableCell>

                              {/* Action */}
                              <SubCategoryTableCell
                                isDragOccurring={snapshot.isDragging}
                                className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                {this.renderAction(key.toString())}
                              </SubCategoryTableCell>
                            </tr>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
            {meta.error ? <InlineError message={meta.error} /> : ''}

            <div className="button-add">
              <Button outline onClick={() => fields.push({})} icon={PlusMinor}>
                {i18n.translate('AdminHTML.Common.Action.addNewWithoutPlus')}
              </Button>
            </div>
          </div>
        </div>
      </DragDropContext>
    );
  }

  getCollectionList = index => {
    const { collections, subCategory } = this.props;
    let result = [...collections];

    if (!_.isEmpty(subCategory)) {
      subCategory.map((item, key) => {
        if (key !== index) {
          _.remove(result, i => i.value === item.collection);
        }
        return '';
      });
    }
    return result;
  };

  renderAction = (key: number) => {
    const { fields } = this.props;

    return fields.length > 1 ? (
      <ButtonGroup segmented>
        <Button outline onClick={() => this.deleteOption(key)} icon={MinusMinor} />
      </ButtonGroup>
    ) : (
      ''
    );
  };

  deleteOption = key => {
    const { fields } = this.props;
    fields.remove(key);
  };
}

const mapDispatchToProps = dispatch => ({
  updateField: (field, value) => dispatch(change(`OptionModalForm`, field, value))
});

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);

  return {
    collectionSubCategory: _.get(formFilterOptionValues, 'collectionSubCategory', [{}])
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SubCategoryTable));
